import { useEffect, useState } from 'react';
import CompaniesPage from '../companies/CompaniesPage.jsx';
import DesignationsPage from '../designations/DesignationsPage.jsx';
import { Settings, User, Shield, Bell, Globe, Palette, Database, Key, Save, Eye, EyeOff } from 'lucide-react';
import { getUser, refreshAuth } from '../../lib/auth.js';
import { usersAPI } from '../../lib/api.js';

// Helper function to format phone number for display (show only 10 digits)
const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // If it's 12 digits and starts with 91, show only the last 10 digits
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) {
    return digitsOnly.substring(2);
  }
  
  // If it's 10 digits, show as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  
  // For other cases, return the original
  return phoneNumber;
};

function SettingsPage() {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('settingsActiveTab') || 'profile';
  });
  useEffect(() => {
    localStorage.setItem('settingsActiveTab', activeTab);
  }, [activeTab]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordErrors, setPasswordErrors] = useState({});
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // User profile state
  const [userProfile, setUserProfile] = useState({
    id: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone_number: '',
    role: '',
    avatar: '',
    loading: true
  });

  // Form state for typing
  const [profileForm, setProfileForm] = useState({
    id: '',
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone_number: '' 
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // Update form when profile data loads
  useEffect(() => {
    if (!userProfile.loading) {
      setProfileForm({
        id: userProfile.id || '',
        firstName: userProfile.firstName,
        middleName: userProfile.middleName || '',
        lastName: userProfile.lastName,
        email: userProfile.email,
        phone_number: formatPhoneForDisplay(userProfile.phone_number)
      });
    }
  }, [userProfile.loading]);

  // Fetch user profile data
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setUserProfile(prev => ({ ...prev, loading: true }));
        
        // Get current user from auth
        const currentUser = getUser();
        console.log('🔍 Current user from auth:', currentUser);
        
        let user = currentUser;
        
        if (!currentUser) {
          // Try to refresh auth data
          const refreshResult = await refreshAuth();
          console.log('🔄 Refresh auth result:', refreshResult);
          if (refreshResult.success) {
            user = refreshResult.user;
            console.log('✅ Fresh user data:', user);
          }
        } else {
          console.log('📋 Using cached user data:', currentUser);
        }
        
        if (user) {
          // Try to get additional profile details by fetching user by ID
          try {
            if (user.id && !isNaN(user.id)) {
              const token = localStorage.getItem('srpf_auth_token');
              console.log('🔍 Fetching detailed profile for user ID:', user.id);
              
              const response = await fetch(`${import.meta.env.VITE_API_URL}/users/${parseInt(user.id)}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const detailedUser = await response.json();
                console.log('📋 Detailed user data:', detailedUser.data);
                user = { ...user, ...detailedUser.data };
              } else {
                console.log('⚠️ Failed to fetch detailed profile, status:', response.status);
              }
            } else {
              console.log('⚠️ Invalid user ID, skipping detailed profile fetch:', user.id);
            }
          } catch (profileError) {
            console.log('⚠️ Could not fetch detailed profile, using basic data:', profileError);
          }
          
          // Parse full_name into first, middle, and last name if not available
          let firstName = '', middleName = '', lastName = '';
          if (user.first_name && user.last_name) {
            // Use separate fields if available
            firstName = user.first_name || '';
            middleName = user.middle_name || '';
            lastName = user.last_name || '';
          } else if (user.full_name) {
            // Parse from full_name - assume middle name if 3+ parts
            const nameParts = user.full_name.split(' ');
            firstName = nameParts[0] || '';
            if (nameParts.length >= 3) {
              middleName = nameParts[1] || '';
              lastName = nameParts.slice(2).join(' ') || '';
            } else {
              lastName = nameParts.slice(1).join(' ') || '';
            }
          }
          
          // Ensure ID is a valid integer
          const userId = user.id ? parseInt(user.id) : null;
          
          setUserProfile({
            id: userId ? userId.toString() : '',
            firstName: firstName,
            middleName: middleName,
            lastName: lastName,
            email: user.email || '',
            phone_number: user.phone_number || user.phone || '',
            role: user.role_name || user.role || '',
            avatar: `https://ui-avatars.com/api/?name=${firstName}+${lastName}&background=6366f1&color=ffffff`,
            loading: false
          });
        } else {
          setUserProfile(prev => ({ ...prev, loading: false }));
        }
      } catch (error) {
        console.error('❌ Failed to fetch user data:', error);
        setUserProfile(prev => ({ ...prev, loading: false }));
      }
    };

    fetchUserProfile();
  }, []);

  // Save profile function
  const handleSaveProfile = async () => {
    try {
      setIsUpdating(true);
      
      // Validate required fields
      if (!profileForm.firstName.trim()) {
        alert('First name is required');
        setIsUpdating(false);
        return;
      }
      if (!profileForm.middleName.trim()) {
        alert('Middle name is required');
        setIsUpdating(false);
        return;
      }
      if (!profileForm.lastName.trim()) {
        alert('Last name is required');
        setIsUpdating(false);
        return;
      }
      if (!profileForm.phone_number.trim()) {
        alert('Phone number is required');
        setIsUpdating(false);
        return;
      }
      
      const updateData = {
        first_name: profileForm.firstName.trim(),
        middle_name: profileForm.middleName.trim(),
        last_name: profileForm.lastName.trim(),
        phone: profileForm.phone_number.trim() // Backend expects 'phone', not 'phone_number'
      };
      
      console.log('💾 Sending profile update data:', updateData);

      const response = await usersAPI.updateProfile(updateData);
      console.log('💾 Profile update response:', response);
      
      // Update the user profile state
      setUserProfile(prev => ({
        ...prev,
        firstName: profileForm.firstName,
        middleName: profileForm.middleName,
        lastName: profileForm.lastName,
        phone_number: profileForm.phone_number,
        avatar: `https://ui-avatars.com/api/?name=${profileForm.firstName}+${profileForm.lastName}&background=6366f1&color=ffffff`
      }));

      alert('Profile updated successfully!');
      
      // Refresh auth data to sync with backend
      await refreshAuth();
      
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async () => {
    try {
      setIsUpdatingPassword(true);
      setPasswordErrors({});

      // Validate form
      if (!passwordForm.currentPassword.trim()) {
        setPasswordErrors({ currentPassword: 'Current password is required' });
        return;
      }
      if (!passwordForm.newPassword.trim()) {
        setPasswordErrors({ newPassword: 'New password is required' });
        return;
      }
      if (passwordForm.newPassword.length < 8) {
        setPasswordErrors({ newPassword: 'New password must be at least 8 characters' });
        return;
      }
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setPasswordErrors({ confirmPassword: 'Passwords do not match' });
        return;
      }
      if (passwordForm.currentPassword === passwordForm.newPassword) {
        setPasswordErrors({ newPassword: 'New password must be different from current password' });
        return;
      }

      const passwordData = {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword
      };

      console.log('🔐 Sending password update data:', { ...passwordData, new_password: '[HIDDEN]' });

      const response = await usersAPI.changePassword(passwordData);
      console.log('🔐 Password update response:', response);

      alert('Password updated successfully!');
      
      // Clear the form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

    } catch (error) {
      console.error('Error updating password:', error);
      
      // Handle specific error messages
      if (error.response?.data?.error?.message) {
        const errorMessage = error.response.data.error.message;
        if (errorMessage.includes('current password')) {
          setPasswordErrors({ currentPassword: 'Current password is incorrect' });
        } else {
          alert(`Failed to update password: ${errorMessage}`);
        }
      } else {
        alert('Failed to update password. Please try again.');
      }
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Handle password form input changes
  const handlePasswordInputChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    // { id: 'notifications', label: 'Notifications', icon: Bell },
    // { id: 'system', label: 'System', icon: Settings },
    { id: 'companies', label: 'Companies', icon: Database },
    { id: 'designations', label: 'Designations', icon: Key }
  ];

  const renderProfileTab = () => {
    if (userProfile.loading) {
      return (
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
            <div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-48 mb-2"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, index) => (
              <div key={index}>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20 mb-2"></div>
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-full"></div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <img
          src={userProfile.avatar}
          alt="Profile"
          className="w-20 h-20 rounded-full object-cover border-4 border-gray-200 dark:border-gray-700"
        />
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {userProfile.firstName} {userProfile.lastName}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">{userProfile.role}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            First Name
          </label>
          <input
            type="text"
            value={profileForm.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Middle Name
          </label>
          <input
            type="text"
            value={profileForm.middleName}
            onChange={(e) => handleInputChange('middleName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Last Name
          </label>
          <input
            type="text"
            value={profileForm.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Email
          </label>
          <input
            type="email"
            value={profileForm.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            value={formatPhoneForDisplay(profileForm.phone_number)}
            onChange={(e) => handleInputChange('phone_number', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
  };

  const renderSecurityTab = () => (
    <div className="space-y-6">
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-center">
          <Shield className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            For security reasons, you'll need to enter your current password to make changes.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Current Password
          </label>
          <div className="relative">
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordForm.currentPassword}
              onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
              className={`w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                passwordErrors.currentPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="Enter your current password"
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {passwordErrors.currentPassword && (
            <p className="text-sm text-red-600 mt-1">{passwordErrors.currentPassword}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordForm.newPassword}
              onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
              className={`w-full px-3 py-2 pr-10 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
                passwordErrors.newPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder="Enter your new password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {passwordErrors.newPassword && (
            <p className="text-sm text-red-600 mt-1">{passwordErrors.newPassword}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Confirm New Password
          </label>
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            value={passwordForm.confirmPassword}
            onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent ${
              passwordErrors.confirmPassword ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            placeholder="Confirm your new password"
          />
          {passwordErrors.confirmPassword && (
            <p className="text-sm text-red-600 mt-1">{passwordErrors.confirmPassword}</p>
          )}
        </div>

        {/* Update Password Button */}
        <div className="pt-4">
          <button
            onClick={handlePasswordUpdate}
            disabled={isUpdatingPassword}
            className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Key className="h-4 w-4" />
            {isUpdatingPassword ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Two-Factor Authentication</h4>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Add an extra layer of security to your account</p>
            <p className="text-xs text-gray-500 dark:text-gray-500">Currently disabled</p>
          </div>
          <button className="px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary/90 transition-colors duration-200">
            Enable 2FA
          </button>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via email</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" defaultChecked className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Push Notifications</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Receive push notifications in the browser</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">SMS Notifications</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Receive notifications via SMS</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-primary"></div>
          </label>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Notification Types</h4>
        <div className="space-y-3">
          {['Appointments', 'Orders', 'Forms', 'Circulars', 'Reminders', 'System Alerts'].map((type) => (
            <div key={type} className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{type}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-brand-primary"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSystemTab = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Auto-save</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Automatically save form data</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" defaultChecked className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-primary/20 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-brand-primary"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Session Timeout</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Automatically log out after inactivity</p>
          </div>
          <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent">
            <option>30 minutes</option>
            <option>1 hour</option>
            <option>2 hours</option>
            <option>Never</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Language</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">Interface language</p>
          </div>
          <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent">
            <option>English</option>
            <option>中文</option>
            <option>Bahasa Melayu</option>
            <option>Tamil</option>
          </select>
        </div>
      </div>

      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Data Management</h4>
        <div className="space-y-3">
          <button className="w-full px-4 py-2 text-left text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200">
            Export my data
          </button>
          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200">
            Delete my account
          </button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileTab();
      case 'security':
        return renderSecurityTab();
      case 'notifications':
        return renderNotificationsTab();
      case 'system':
        return renderSystemTab();
      case 'companies':
        return <CompaniesPage />;
      case 'designations':
        return <DesignationsPage />;
      default:
        return renderProfileTab();
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your account settings and preferences
          </p>
        </div>
        
        <button 
          onClick={handleSaveProfile}
          disabled={isUpdating || userProfile.loading}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="mr-2 h-4 w-4" />
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Settings Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </div>
              </button>
            ))}
            
          </nav>
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
